// Audio Book Recommendation Engine
// Tag-based similarity, age filtering, diversity, and exploration

import { parseHashtags, categorizeTheme, parseAgeGroup, AGE_GROUPS } from './audio-book-tags';

export interface AudioBook {
  id: string;
  title: string;
  hashtags: string;
  ageBucket: string | null;
  coverImageUrl: string | null;
  pageCount: number;
  totalDuration: number | null;
  language: string;
  isFeatured: boolean;
  isFeaturedSample?: boolean;
  montessoriMode: boolean;
  createdAt: Date | string;
}

export interface UserProfile {
  tags: Map<string, number>;
  themes: Set<string>;
  agePreferences: string[];
}

/**
 * Build user interest profile from recent watches and favorites
 */
export function buildUserProfile(
  watchedBooks: Array<{ hashtags: string; ageBucket: string | null }>,
  favoritedBooks: Array<{ hashtags: string; ageBucket: string | null }>
): UserProfile {
  const tagFrequency = new Map<string, number>();
  const themes = new Set<string>();
  const agePreferences: string[] = [];

  // Process watched books (weight: 1x)
  watchedBooks.forEach(book => {
    const tags = parseHashtags(book.hashtags);
    tags.forEach(tag => {
      tagFrequency.set(tag, (tagFrequency.get(tag) || 0) + 1);
    });
    
    const bookThemes = categorizeTheme(tags);
    bookThemes.forEach(theme => themes.add(theme));
    
    if (book.ageBucket) {
      const ageGroup = parseAgeGroup(book.ageBucket);
      if (!agePreferences.includes(ageGroup)) {
        agePreferences.push(ageGroup);
      }
    }
  });

  // Process favorited books (weight: 2x)
  favoritedBooks.forEach(book => {
    const tags = parseHashtags(book.hashtags);
    tags.forEach(tag => {
      tagFrequency.set(tag, (tagFrequency.get(tag) || 0) + 2);
    });
    
    const bookThemes = categorizeTheme(tags);
    bookThemes.forEach(theme => themes.add(theme));
    
    if (book.ageBucket) {
      const ageGroup = parseAgeGroup(book.ageBucket);
      if (!agePreferences.includes(ageGroup)) {
        agePreferences.push(ageGroup);
      }
    }
  });

  return { tags: tagFrequency, themes, agePreferences };
}

/**
 * Compute cosine similarity between user profile and candidate book
 */
export function computeSimilarity(
  userProfile: UserProfile,
  candidateBook: { hashtags: string }
): number {
  const candidateTags = parseHashtags(candidateBook.hashtags);
  const candidateThemes = new Set(categorizeTheme(candidateTags));
  
  if (candidateTags.length === 0) return 0;
  
  // Tag-based cosine similarity
  let dotProduct = 0;
  let userMagnitude = 0;
  let candidateMagnitude = 0;
  
  // Calculate user profile magnitude
  userProfile.tags.forEach(weight => {
    userMagnitude += weight * weight;
  });
  userMagnitude = Math.sqrt(userMagnitude);
  
  // Calculate candidate magnitude and dot product
  const candidateTagFreq = new Map<string, number>();
  candidateTags.forEach(tag => {
    candidateTagFreq.set(tag, (candidateTagFreq.get(tag) || 0) + 1);
  });
  
  candidateTagFreq.forEach((candWeight, tag) => {
    candidateMagnitude += candWeight * candWeight;
    const userWeight = userProfile.tags.get(tag) || 0;
    dotProduct += userWeight * candWeight;
  });
  candidateMagnitude = Math.sqrt(candidateMagnitude);
  
  // Cosine similarity
  let similarity = 0;
  if (userMagnitude > 0 && candidateMagnitude > 0) {
    similarity = dotProduct / (userMagnitude * candidateMagnitude);
  }
  
  // Boost for theme overlap (up to +0.3)
  let themeBoost = 0;
  candidateThemes.forEach(theme => {
    if (userProfile.themes.has(theme)) {
      themeBoost += 0.1;
    }
  });
  themeBoost = Math.min(themeBoost, 0.3);
  
  return Math.min(similarity + themeBoost, 1.0);
}

/**
 * Filter books by age overlap
 */
export function filterByAge(
  books: Array<AudioBook>,
  userAgeMin: number,
  userAgeMax: number
): Array<AudioBook> {
  return books.filter(book => {
    if (!book.ageBucket) return true; // Include "all ages" books
    
    // Extract age range from ageBucket
    const numbers = book.ageBucket.match(/\d+/g);
    if (!numbers || numbers.length === 0) return true;
    
    const bookAgeMin = parseInt(numbers[0]);
    const bookAgeMax = numbers.length > 1 ? parseInt(numbers[1]) : bookAgeMin;
    
    // Check for overlap: [userMin, userMax] overlaps [bookMin, bookMax]
    return userAgeMin <= bookAgeMax && userAgeMax >= bookAgeMin;
  });
}

/**
 * Apply Maximal Marginal Relevance (MMR) for diversity
 * Ensures variety by penalizing books too similar to already selected ones
 */
export function applyDiversity(
  rankedBooks: Array<AudioBook>,
  diversityFactor: number = 0.3
): Array<AudioBook> {
  if (rankedBooks.length <= 5) return rankedBooks; // No need for diversity on small sets
  
  const selected: AudioBook[] = [];
  const remaining = [...rankedBooks];
  
  // Select first book (highest score)
  if (remaining.length > 0) {
    selected.push(remaining.shift()!);
  }
  
  // Iteratively select books that balance relevance and diversity
  while (remaining.length > 0 && selected.length < rankedBooks.length) {
    let bestScore = -1;
    let bestIndex = 0;
    
    remaining.forEach((candidate, index) => {
      // Calculate max similarity to already selected books
      let maxSimilarity = 0;
      selected.forEach(selectedBook => {
        const sim = computeTagSimilarity(candidate, selectedBook);
        maxSimilarity = Math.max(maxSimilarity, sim);
      });
      
      // MMR score: balance relevance (position) and diversity
      const relevance = 1 - (index / remaining.length); // Favor earlier (higher ranked) books
      const mmrScore = diversityFactor * relevance - (1 - diversityFactor) * maxSimilarity;
      
      if (mmrScore > bestScore) {
        bestScore = mmrScore;
        bestIndex = index;
      }
    });
    
    selected.push(remaining.splice(bestIndex, 1)[0]);
  }
  
  return selected;
}

/**
 * Helper: Compute tag similarity between two books
 */
function computeTagSimilarity(book1: AudioBook, book2: AudioBook): number {
  const tags1 = new Set(parseHashtags(book1.hashtags));
  const tags2 = new Set(parseHashtags(book2.hashtags));
  
  if (tags1.size === 0 || tags2.size === 0) return 0;
  
  // Jaccard similarity
  const intersection = new Set([...tags1].filter(tag => tags2.has(tag)));
  const union = new Set([...tags1, ...tags2]);
  
  return intersection.size / union.size;
}

/**
 * Inject exploration: replace a percentage of slots with high-quality new books
 */
export function injectExploration(
  personalizedBooks: Array<AudioBook>,
  newBooks: Array<AudioBook>,
  explorePercent: number = 0.15
): Array<AudioBook> {
  const exploreCount = Math.floor(personalizedBooks.length * explorePercent);
  
  if (exploreCount === 0 || newBooks.length === 0) {
    return personalizedBooks;
  }
  
  // Create a set of existing book IDs to avoid duplicates
  const existingIds = new Set(personalizedBooks.map(b => b.id));
  
  // Filter new books to exclude any already in personalizedBooks
  const uniqueNewBooks = newBooks.filter(b => !existingIds.has(b.id));
  
  if (uniqueNewBooks.length === 0) {
    return personalizedBooks;
  }
  
  // Shuffle unique new books and take top `exploreCount`
  const shuffled = [...uniqueNewBooks].sort(() => Math.random() - 0.5);
  const exploratory = shuffled.slice(0, exploreCount);
  
  // Replace random slots in personalized books
  const result = [...personalizedBooks];
  const replaceIndices = new Set<number>();
  
  while (replaceIndices.size < Math.min(exploreCount, result.length / 2)) {
    const randomIndex = Math.floor(Math.random() * result.length);
    replaceIndices.add(randomIndex);
  }
  
  const replaceArray = Array.from(replaceIndices).sort((a, b) => a - b);
  replaceArray.forEach((index, i) => {
    if (i < exploratory.length) {
      result[index] = exploratory[i];
    }
  });
  
  return result;
}

/**
 * Deduplicate books by ID - ensures no duplicates within a single rail
 */
export function deduplicateBooks(books: Array<AudioBook>): Array<AudioBook> {
  const seen = new Set<string>();
  return books.filter(book => {
    if (seen.has(book.id)) return false;
    seen.add(book.id);
    return true;
  });
}

/**
 * Rank books by multiple factors: similarity, popularity, recency
 */
export function rankBooks(
  books: Array<AudioBook>,
  userProfile: UserProfile,
  popularityScores?: Map<string, number>,
  preferredLanguage?: string
): Array<{ book: AudioBook; score: number }> {
  return books.map(book => {
    // Similarity score (0-1)
    const similarity = computeSimilarity(userProfile, book);
    
    // Popularity boost (0-0.2)
    const popularityBoost = popularityScores?.get(book.id) || 0;
    
    // Recency boost (0-0.1) - newer books get slight boost
    const daysSinceCreation = (Date.now() - new Date(book.createdAt).getTime()) / (1000 * 60 * 60 * 24);
    const recencyBoost = Math.max(0, 0.1 - (daysSinceCreation / 365) * 0.1);
    
    // Featured boost (0-0.1)
    const featuredBoost = book.isFeatured ? 0.1 : 0;
    
    // Language preference boost (0-0.15) - boost books in user's preferred language
    const languageBoost = preferredLanguage && book.language === preferredLanguage ? 0.15 : 0;
    
    // Total score (adjusted weights to accommodate language boost)
    const score = similarity * 0.65 + popularityBoost * 0.15 + recencyBoost * 0.1 + featuredBoost * 0.05 + languageBoost;
    
    return { book, score };
  }).sort((a, b) => b.score - a.score);
}

/**
 * Extract age range from user profile or ageBucket string
 */
export function extractAgeRange(ageBucket: string | null): { min: number; max: number } {
  if (!ageBucket) return { min: 0, max: 12 }; // Default to all ages
  
  const numbers = ageBucket.match(/\d+/g);
  if (!numbers || numbers.length === 0) return { min: 0, max: 12 };
  
  const min = parseInt(numbers[0]);
  const max = numbers.length > 1 ? parseInt(numbers[1]) : min;
  
  return { min, max };
}

